/**
 * "What's new in NU Assist" — the home page carousel's slides and the pure
 * rules around them (Jose, 2026-10-08, artifact v3 section 3; four slides,
 * no pill and specific links on artifact v8, 2026-10-09).
 *
 * Each slide shows from its `since` for `PILL_WINDOW_DAYS`, through the same
 * `isAnnouncementActive` the sidebar's "new" pills use, so a slide and the
 * pill on the page it announces go quiet on the same day. When no slide is
 * active the carousel renders nothing.
 */
import type { WhatsNewLinks } from "@nu-assist/contracts";

import { personInListPath } from "../../features/person-panel/personHandle";
import { searchDetailPath } from "../../routing/paths";
import {
  LEFT_MENU_SECTIONS,
  PILL_WINDOW_DAYS,
  isAnnouncementActive,
} from "../../shell/leftMenuModel";

export type WhatsNewIllustration =
  | "bullseye"
  | "combine-filter"
  | "ai-review"
  | "chopbook"
  | "thrive-import"
  | "four-reports";

/**
 * Where a slide's link goes, from the most specific target to the most
 * general one. The specific targets come from `GET /api/web/v2/whats-new/links`
 * (`WhatsNewLinks`); until it answers, or when it fails or finds
 * nothing, the link takes its general fallback.
 *
 * `search-tab`: a tab of the search the endpoint names for `tab`, else My
 *   Searches. `pageId` is the tab's feature-flag id; the host page is
 *   `dash-searches`.
 * `page`: a page of the left menu, by id. Its section and path are read
 *   from `LEFT_MENU_SECTIONS` (`menuPage`), never copied here.
 * `reviewed-person`: the AI Review tab of a person the endpoint names, on the
 *   AI Review list that scored them (`/research/imports/<id>?person=…`). A
 *   reader who cannot see the in-list person panel gets the list itself; with
 *   no answer, the `pageId` page.
 * `in-page`: a page that is not a left-menu item, at `path`, gated by the
 *   in-page feature id `pageId` under `sectionId` (the New Import wizard's
 *   Thrive source) AND by the menu page `hostPageId` that leads to it.
 * All are gated like the left menu, and a slide whose link the reader cannot
 * open is not shown at all (`resolveSlideHref` → `null`), the rule the menu's
 * "new" pills follow.
 */
export type WhatsNewLink =
  | { kind: "search-tab"; label: string; tab: "bullseye" | "candidates"; pageId: string }
  | { kind: "page"; label: string; pageId: string }
  | { kind: "reviewed-person"; label: string; pageId: string }
  | {
      kind: "in-page";
      label: string;
      path: string;
      sectionId: string;
      pageId: string;
      hostPageId: string;
    };

/** A second link under the first; always a plain page of the left menu. */
export type WhatsNewSecondaryLink = Extract<WhatsNewLink, { kind: "page" }>;

export interface WhatsNewSlide {
  id: string;
  title: string;
  /** Optional opening words of the body, set in bold; `body` follows it. */
  lead?: string;
  body: string;
  illustration: WhatsNewIllustration;
  /**
   * Product artwork shown with the text (a `public/` path). Decorative: the
   * title names the product, so it renders with an empty alt.
   */
  artwork?: { src: string };
  link: WhatsNewLink;
  /**
   * A second link. The slide shows only when the reader can open BOTH
   * targets (`resolveSlideLinks`).
   */
  secondaryLink?: WhatsNewSecondaryLink;
  /** A fine-print line under the link (the Candidates preview's sample data). */
  note?: string;
  /** First day the slide shows, `YYYY-MM-DD`; it shows for `PILL_WINDOW_DAYS`. */
  since: string;
}

export const WHATS_NEW_SLIDES: readonly WhatsNewSlide[] = [
  {
    id: "bullseye",
    title: "Bullseye",
    body: "You can convert one or more AI Review lists into a Bullseye view for pitches and execution. Show the client how the candidates compare to the assessment criteria.",
    illustration: "bullseye",
    link: {
      kind: "search-tab",
      label: "Create a Bullseye",
      tab: "bullseye",
      pageId: "bullseye",
    },
    since: "2026-10-08",
  },
  {
    id: "combine-filter",
    title: "Combine & Filter",
    body: "You can combine, filter, and separate one or more People Lists, no matter what kind of list it is.",
    illustration: "combine-filter",
    link: { kind: "page", label: "Open Combine & Filter", pageId: "combine-filter" },
    since: "2026-10-08",
  },
  {
    // The four candidate reports (epic 2026-10-22-candidate-reports-four-kinds),
    // released 2026-10-26: replaced the 2026-10-08 slide ("Reference and
    // Assessment reports now available", a sample assessment report), which
    // announced two kinds. Dated to the release day, so it shows as new again.
    // The illustration is a schematic of the candidate panel: the four kinds
    // and Download in one box (2026-10-27, replacing a dark screenshot), on
    // the thrive-mock's fictional candidate.
    id: "candidates",
    title: "Four candidate reports",
    body: "Recruiter and Client assessments, Official and Backchannel references, straight from Thrive. Pick a report and a format, then Download — in the candidate panel or from the row’s Reports button.",
    illustration: "four-reports",
    link: {
      kind: "search-tab",
      label: "See a search with assessments",
      tab: "candidates",
      pageId: "search-candidates",
    },
    note: "Sample panel with a fictional candidate.",
    since: "2026-10-26",
  },
  {
    id: "ai-review",
    title: "AI Review now pulls in data from Thrive",
    body: "Before reviewing a candidate, AI Review now reads our Thrive notes and assessments on them.",
    illustration: "ai-review",
    link: { kind: "reviewed-person", label: "Open your latest AI Review", pageId: "ai-review" },
    since: "2026-10-08",
  },
  {
    // Gated by its two pages, `company-lists` and `tool-chopbook`, both
    // released 2026-10-20 (the ChopBook release).
    id: "chopbook",
    title: "ChopBook and Company Lists",
    lead: "ChopBook is a new Chrome extension",
    // Continues the sentence the bold `lead` starts: the two render side by
    // side with no separator, so `body` begins with the comma.
    body: ", separate from ChopLink, installed from its own Chrome Web Store listing. On a PitchBook results page, select the companies you want and ChopBook imports them into NU Assist as a Company List for your search, right next to its People Lists. Each company gets one record, with its description, industry, headquarters, financials and logo, and shows the people you already know there.",
    illustration: "chopbook",
    artwork: { src: "/chopbook-librarian.png" },
    link: { kind: "page", label: "Open Company Lists", pageId: "company-lists" },
    secondaryLink: { kind: "page", label: "How to install ChopBook", pageId: "tool-chopbook" },
    // Dated to the release day: the month starts the day recruiters can first
    // open the pages, like `CHOPBOOK_NEW_BADGE` in `leftMenuModel.ts`.
    since: "2026-10-20",
  },
  {
    // Gated by the in-page feature `thrive-search-import`, released
    // 2026-10-20 (epic 2026-10-15-thrive-search-import).
    id: "thrive-import",
    // Only the import (Jose, 2026-10-20): Combine & Filter keeps its own slide,
    // so this one does not draw or link it.
    title: "Import a Thrive search",
    lead: "People Lists can now start from Thrive",
    body: ". Besides ChopLink and uploaded files, pick an existing Thrive search and the stages you want: its candidates become a raw People List tagged Thrive, with each person’s stage, days in stage and date added. Added to a Thrive search from there, they start as Identified.",
    illustration: "thrive-import",
    link: {
      kind: "in-page",
      label: "Import a Thrive search",
      path: "/research/imports/new",
      sectionId: "linkedin",
      pageId: "thrive-search-import",
      hostPageId: "my-imports",
    },
    // Dated to the release day, same rule as the ChopBook slide above.
    since: "2026-10-20",
  },
];

/** The slides inside their window today, in list order. */
export function activeWhatsNewSlides(
  slides: readonly WhatsNewSlide[],
  today?: string
): WhatsNewSlide[] {
  return slides.filter((s) => isAnnouncementActive({ since: s.since }, today));
}

/** A `search-tab` link's href: the search's tab, or My Searches without one. */
export function searchTabHref(
  tab: "bullseye" | "candidates",
  searchId: string | null,
  mySearchesHref: string
): string {
  return searchId === null ? mySearchesHref : `${searchDetailPath(searchId)}?tab=${tab}`;
}

/** A left-menu page's section and path, or `null` for an id the menu lacks. */
export function menuPage(pageId: string): { sectionId: string; path: string } | null {
  for (const section of LEFT_MENU_SECTIONS) {
    const item = section.items.find((i) => i.id === pageId);
    if (item) return { sectionId: item.sectionId, path: item.path };
  }
  return null;
}

/** The host page of every `search-tab` link: Search Detail, under My Searches. */
const SEARCH_HOST = { sectionId: "client-engagement", pageId: "dash-searches" } as const;

/** The page that hosts an AI Review list, and with it the person's panel. */
const PEOPLE_LISTS_PAGE_ID = "my-imports";

/**
 * The in-list person panel's gate, the one `ImportDetailPage` applies before
 * it opens `?person=` (released on dev; on prod only from `?preview=2`).
 */
const PERSON_PANEL = { sectionId: "linkedin", pageId: "person-panel" } as const;

/**
 * Where the AI Review slide sends a reader for a list the endpoint named: the
 * person's AI Review tab on that list when the reader can see the person
 * panel (built by the panel's own `personInListPath`), else the list itself,
 * with no `person` or `tab` (the page would ignore them).
 */
export function reviewedPersonHref(
  listsPath: string,
  importId: string,
  person: string,
  personPanelVisible: boolean
): string {
  return personPanelVisible
    ? personInListPath(importId, person, "ai-review")
    : `${listsPath}/${encodeURIComponent(importId)}`;
}

/** `YYYY-MM-DD` plus `days`. */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The first day on which none of `slides` is in its window: the latest
 * `since` plus `PILL_WINDOW_DAYS`. Lets tests (and anyone asking "when does
 * the carousel go away?") follow a re-dated slide instead of a literal.
 */
export function whatsNewOverOn(slides: readonly WhatsNewSlide[]): string {
  const last = slides.reduce((max, s) => (s.since > max ? s.since : max), "");
  return addDays(last, PILL_WINDOW_DAYS);
}

/** A slide's whole body text, the bold lead included. */
export function slideBodyText(slide: WhatsNewSlide): string {
  return `${slide.lead ?? ""}${slide.body}`;
}

/**
 * Both of a slide's hrefs, or `null` when the reader cannot open one of its
 * targets: a slide with two links shows only when both open.
 */
export function resolveSlideLinks(
  slide: WhatsNewSlide,
  isVisible: (sectionId: string, pageId: string) => boolean,
  links: WhatsNewLinks | undefined,
  mySearchesHref: string
): { href: string; secondaryHref: string | null } | null {
  const href = resolveSlideHref(slide.link, isVisible, links, mySearchesHref);
  if (href === null) return null;
  if (slide.secondaryLink === undefined) return { href, secondaryHref: null };
  const secondaryHref = resolveSlideHref(slide.secondaryLink, isVisible, links, mySearchesHref);
  return secondaryHref === null ? null : { href, secondaryHref };
}

/**
 * A slide's href, or `null` when the reader cannot open its target (the
 * slide is then not shown). `isVisible` is the left menu's gate
 * (`useFeatureAccess().page(section, page).visible`). `links` is the
 * endpoint's answer, `undefined` until it arrives or when it fails; only the
 * href depends on it, never whether the slide shows.
 */
export function resolveSlideHref(
  link: WhatsNewLink,
  isVisible: (sectionId: string, pageId: string) => boolean,
  links: WhatsNewLinks | undefined,
  mySearchesHref: string
): string | null {
  if (link.kind === "search-tab") {
    if (!isVisible(SEARCH_HOST.sectionId, SEARCH_HOST.pageId)) return null;
    if (!isVisible(SEARCH_HOST.sectionId, link.pageId)) return null;
    const searchId = links?.[link.tab]?.search_id_thrive ?? null;
    return searchTabHref(link.tab, searchId, mySearchesHref);
  }
  if (link.kind === "in-page") {
    const host = menuPage(link.hostPageId);
    if (host === null || !isVisible(host.sectionId, link.hostPageId)) return null;
    return isVisible(link.sectionId, link.pageId) ? link.path : null;
  }
  const page = menuPage(link.pageId);
  if (page === null || !isVisible(page.sectionId, link.pageId)) return null;
  if (link.kind === "page") return page.path;
  const reviewed = links?.ai_review ?? null;
  const lists = menuPage(PEOPLE_LISTS_PAGE_ID);
  if (reviewed === null || lists === null || !isVisible(lists.sectionId, PEOPLE_LISTS_PAGE_ID)) {
    return page.path;
  }
  return reviewedPersonHref(
    lists.path,
    reviewed.import_id,
    reviewed.person,
    isVisible(PERSON_PANEL.sectionId, PERSON_PANEL.pageId)
  );
}
